import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { Article, TextSection } from "@domain/lms/article/article";
import { getDisplayedAuthor } from "@domain/lms/utils";
import { theme } from "@ui/styles/theme";
import { gap } from "@ui/styles/utils";
import { row } from "@ui/components/layout";
import { UserView } from "@ui/components/profile/userView";
//import { nFirstCharOnly } from "../../../utils/strings";
import moment from "moment";
import { useIsMobile } from "@utils/hooks";
import { truncString } from "@utils/strings";
import { CalendarIconBis, EyeIcon, TimeIcon } from "@ui/components/icons";
import { SubAuthor } from "@ui/components/subAuthor";
import { ArticleThumbnailHeader } from "./articleThumbnailHeader";

interface ArticleThumbnailProps extends React.HTMLAttributes<HTMLDivElement> {
	article: Article;
	displayAuthorAsMe?: boolean;
	forFeed?: boolean;
	small?: boolean;
	displayStatus?: boolean;
}
export const ArticleThumbnail: React.FC<ArticleThumbnailProps> = ({
	article,
	displayAuthorAsMe,
	forFeed = false,
	small: forceSmall,
	displayStatus,
}) => {
	const { author } = article;
	const isMobile = useIsMobile();
	const displayedAuthor = getDisplayedAuthor(article);
	const subAuthor = displayedAuthor !== author ? author : undefined;
	const smallVersion = forceSmall || isMobile;
	//const displayedInsightMobileHeader = forFeed || hideAnimation || showKebab;
	const MAX_TEMPER = 260;
	const articleContent = article?.content?.sections
		?.filter((s): s is TextSection => s.type === "TEXT" && !!s.content)
		.map((s) => s.content.getPlainText())
		.join();
	return (
		<Container>
			{/* {displayedInsightMobileHeader && isMobile ? (
				<ArticleThumbnailSmallHeader article={article} isSmall={smallVersion} showKebab />
			) : (
				<ArticleThumbnailHeader article={article} isSmall={smallVersion} />
			)} */}
			<ArticleThumbnailHeader displayStatus={displayStatus} article={article} isSmall={smallVersion} />
			<Link to={Path.ReferenceArticle({ id: article.id, slug: article.slug, category: article.category })}>
				<ArticleContainer forFeed={forFeed}>
					<StyledContainer>
						<StyledSubdiv>
							<div>
								<EyeIcon color={theme.colors.black} />
								<span>{article.views.count}</span>
							</div>
							<div>
								<TimeIcon color={theme.colors.black} />
								<span>{article.duration} min</span>
							</div>
							<div>
								<CalendarIconBis color={theme.colors.black} />
								<span>{moment(article.createdAt).format("D MMM YYYY")}</span>
							</div>
						</StyledSubdiv>
						<ArticleTitle forFeed={forFeed}>{article.title}</ArticleTitle>
						<ArticleDescription>{truncString(articleContent, MAX_TEMPER)}</ArticleDescription>
						<BottomInfos>
							<StyledUserView
								unClickable
								user={displayedAuthor}
								alignStart={!!subAuthor}
								renderBottomAccessory={
									subAuthor ? () => <SubAuthor author={subAuthor} displayAuthorAsMe={displayAuthorAsMe} /> : undefined
								}
							/>
						</BottomInfos>
					</StyledContainer>
				</ArticleContainer>
			</Link>
		</Container>
	);
};

const Container = styled.div`
	position: relative;
	border-radius: 13px;
`;

const ArticleContainer = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "forFeed",
})<{ forFeed: boolean }>`
	display: flex;
	flex-direction: column;
	position: relative;
	background-color: white;
	overflow: hidden;
	border-bottom-left-radius: 13px;
	border-bottom-right-radius: 13px;
	height: 240px;
	${({ forFeed }) => (forFeed ? "border: solid 1px #eaebef" : undefined)};
`;
const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	margin-left: 20px;
`;

const ArticleTitle = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "forFeed",
})<{ forFeed: boolean }>`
	${({ theme }) => theme.textStyles.desktopSubtitle};
	${({ forFeed }) => (forFeed ? "" : "height: 50px !important ")}
	/* max-height: 50px;
	min-height: 50px; */
	word-break: break-word;
	display: -webkit-box;
	overflow: hidden;
	text-overflow: ellipsis;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	line-height: 1.3;
	padding-top: 0;
	margin-bottom: 1px;
	font-size: 18px;
	color: #000000;
	font-family: Sora;
	font-style: normal;
	font-weight: 600;
	margin-top: 20px;

	/* @media (max-width: 900px) {
		max-height: 39px;
		margin: 4px 0 10px 0;
	} */
`;

const ArticleDescription = styled.div`
	//width: 90%;
	max-height: 57px;
	min-height: 74px;
	overflow-wrap: break-word;
	overflow: hidden;
	font-size: 14px;
	line-height: 18px;
	text-overflow: ellipsis;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	margin-top: 10px;
	color: #000000;

	@media (max-width: 900px) {
		width: 100%;
	}
`;

const StyledSubdiv = styled.div`
	display: flex;
	font-weight: 300 !important;
	${({ theme }) => theme.textStyles.text};
	${gap(18, "row")}

	> div {
		display: flex;
		align-items: center;
		vertical-align: center;

		span {
			margin-top: 2px;
		}
	}

	svg {
		margin-right: 2px;
	}
	margin-top: 17px;
`;

const BottomInfos = styled.div`
	align-self: stretch;
	${row(10, "space-between")};
	max-height: 50px;
	min-height: 50px;
	margin-top: 10px;
	margin-bottom: 10px;
`;
const StyledUserView = styled(UserView)`
	color: ${({ theme }) => theme.colors.black};
`;
